import type { ColumnDef, ColumnFiltersState, RowData, SortingState } from '@tanstack/react-table';
import type { OrderBy } from '@/lib/graphql-query-builder';
import {
  dateOnlyOf,
  dateOnlyRangeClause,
  nextDateOnlyDay,
  orgCalendarDate,
  orgDayStartToUtc,
  orgWallClock,
  orgWallTimeToUtc,
} from '@/lib/utils/custom-date/custom-date';

/**
 * Server-side sorting and column filtering for `AdvanceDataTable`. A sortable or filterable column's
 * `id` is the schema field it shows (`id: 'Title'`), so the table state maps straight onto the Data
 * Gateway's `order` and `where`. A column that shows something without a field of its own (a computed
 * value, a name looked up from `UserId`) sets `enableSorting: false` and has no `meta.filter`.
 */

export interface ColumnFilterOption {
  value: string | number | boolean;
  label: string;
}

/**
 * Which filter a column header offers:
 * - `text`: contains, case-insensitive;
 * - `options`: one of the listed values (checkboxes, or with `dropdown` a single pick from a
 *   searchable dropdown, for long lists such as departments);
 * - `date`: a day range on a date-only field (stored at 00:00:00 UTC);
 * - `datetime`: a day range in the organization's zone on a UTC moment field (`...Utc`, `CreatedDate`);
 * - `number`: a min/max range;
 * - `time`: a time-of-day range ("HH:mm", the organization's clock) on a UTC moment field. A server
 *   list sets `withDays`: the server can't read a moment's time of day, so the filter also takes a
 *   day range (up to `MAX_TIME_FILTER_DAYS`) and asks for that time window on each of those days.
 *   A `to` before `from` (22:00 to 02:00) runs past midnight.
 */
export type ColumnFilterConfig =
  | { type: 'text' }
  | { type: 'options'; options: ColumnFilterOption[]; dropdown?: boolean }
  | { type: 'date' }
  | { type: 'datetime' }
  | { type: 'number' }
  | { type: 'time'; withDays?: boolean };

/** The most days a server-side time filter spans (one `or` clause per day). */
export const MAX_TIME_FILTER_DAYS = 62;

/**
 * Where a column goes on the card `AdvanceDataTable` shows a row as on a phone:
 * - `title` / `subtitle`: the card's heading and the muted line under it;
 * - `badge`: top right, next to the title (a status chip);
 * - `body`: a label/value pair, two per line (to move a column the default puts elsewhere);
 * - `full`: a label/value pair across the whole card (long text such as a reason);
 * - `footer`: under a dashed line, right-aligned (buttons);
 * - `hidden`: not on the card.
 * Without one: the first data column is the title, `Status` the badge, `actions` the footer and the
 * rest label/value pairs, two per line.
 */
export type MobileCardRole = 'title' | 'subtitle' | 'badge' | 'body' | 'full' | 'footer' | 'hidden';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Shows a filter icon in the column header (see `ColumnFilterConfig`). */
    filter?: ColumnFilterConfig;
    /** The column's place on the mobile card (see `MobileCardRole`). */
    mobile?: MobileCardRole;
    /** The label/value pairs of the card go by this, lowest first; pairs without one follow, in
     * column order. */
    mobileOrder?: number;
    /** Shown on the mobile card only, never as a table column (e.g. a value a table column
     * already shows inside another cell). */
    cardOnly?: boolean;
    /** Right-aligned in tabular figures (true) or not (false); detected from the values when left out. */
    numeric?: boolean;
  }
}

/** A column filter's value, as the header's filter popover sets it. */
export type ColumnFilterValue =
  | { type: 'text'; value: string }
  | { type: 'options'; values: ColumnFilterOption['value'][] }
  | { type: 'date' | 'datetime'; from?: string; to?: string }
  | { type: 'number'; min?: number; max?: number }
  | { type: 'time'; from?: string; to?: string; fromDay?: string; toDay?: string };

/** Whether a filter value would filter anything (an empty one is cleared instead of applied). */
export const isFilterValueActive = (value: ColumnFilterValue | undefined): boolean => {
  if (!value) return false;
  switch (value.type) {
    case 'text':
      return value.value.trim() !== '';
    case 'options':
      return value.values.length > 0;
    case 'date':
    case 'datetime':
      return Boolean(value.from || value.to);
    case 'number':
      return value.min !== undefined || value.max !== undefined;
    case 'time':
      return Boolean(value.from || value.to);
    default:
      return false;
  }
};

const orgDayRangeClause = (from?: string, to?: string) => {
  const range: { $gte?: string; $lt?: string } = {};
  if (from) range.$gte = orgDayStartToUtc(from);
  if (to) range.$lt = orgDayStartToUtc(nextDateOnlyDay(to));
  return Object.keys(range).length > 0 ? range : undefined;
};

const parseTime = (time: string): [number, number] => {
  const [hour, minute] = time.split(':').map(Number);
  return [hour, minute];
};

/** One `{ field: { $gte, $lt } }` per day of the filter's day range: the time window on that day. */
const timeOfDayClauses = (
  field: string,
  value: Extract<ColumnFilterValue, { type: 'time' }>
): Record<string, unknown>[] | undefined => {
  if (!value.fromDay || !value.toDay) return undefined;
  const [fromHour, fromMinute] = parseTime(value.from || '00:00');
  const [toHour, toMinute] = parseTime(value.to || '23:59');
  const pastMidnight = Boolean(value.from && value.to && value.to < value.from);
  const clauses: Record<string, unknown>[] = [];
  for (
    let day = value.fromDay;
    day <= value.toDay && clauses.length < MAX_TIME_FILTER_DAYS;
    day = nextDateOnlyDay(day)
  ) {
    const endDay = pastMidnight ? nextDateOnlyDay(day) : day;
    clauses.push({
      [field]: {
        $gte: orgWallTimeToUtc(day, fromHour, fromMinute),
        // The `to` minute is included: the window ends at the start of the next minute.
        $lt: orgWallTimeToUtc(endDay, toHour, toMinute + 1),
      },
    });
  }
  return clauses;
};

const filterClause = (value: ColumnFilterValue): unknown => {
  switch (value.type) {
    case 'text':
      return { $regex: value.value.trim(), $options: 'i' };
    case 'options':
      // Boolean fields have no `in` on the gateway (BooleanOperationFilterInput): one value is an
      // equality, both values filter nothing.
      if (value.values.every((option) => typeof option === 'boolean')) {
        return new Set(value.values).size === 1 ? value.values[0] : undefined;
      }
      return { $in: value.values };
    case 'date':
      return dateOnlyRangeClause(value.from, value.to);
    case 'datetime':
      return orgDayRangeClause(value.from, value.to);
    case 'number': {
      const range: { $gte?: number; $lte?: number } = {};
      if (value.min !== undefined) range.$gte = value.min;
      if (value.max !== undefined) range.$lte = value.max;
      return range;
    }
    default:
      return undefined;
  }
};

/** The column filters as a Mongo-style filter object (`buildWhereLiteral` shapes it), keyed by field. */
export const buildColumnFiltersWhere = (filters: ColumnFiltersState): Record<string, unknown> => {
  const where: Record<string, unknown> = {};
  const and: Record<string, unknown>[] = [];
  for (const { id, value } of filters) {
    const filterValue = value as ColumnFilterValue | undefined;
    if (!isFilterValueActive(filterValue)) continue;
    if (filterValue?.type === 'time') {
      const clauses = timeOfDayClauses(id, filterValue);
      if (clauses) and.push({ $or: clauses });
      continue;
    }
    const clause = filterClause(filterValue as ColumnFilterValue);
    if (clause !== undefined) where[id] = clause;
  }
  if (and.length > 0) where.$and = and;
  return where;
};

/**
 * A list's own filter combined with its column filters. A field both filter on keeps both clauses
 * (joined with `$and`) instead of the column filter replacing the list's, so a column filter can only
 * narrow what the list may show.
 */
export const mergeWhere = (
  base: Record<string, unknown> = {},
  extra: Record<string, unknown> = {}
): Record<string, unknown> => {
  const andClauses = (where: Record<string, unknown>) =>
    Array.isArray(where.$and) ? (where.$and as Record<string, unknown>[]) : [];
  const merged: Record<string, unknown> = { ...base };
  const shared: Record<string, unknown>[] = [];
  for (const [field, clause] of Object.entries(extra)) {
    if (field === '$and') continue;
    if (field in base) shared.push({ [field]: clause });
    else merged[field] = clause;
  }
  const and = [...andClauses(base), ...andClauses(extra), ...shared];
  if (and.length > 0) merged.$and = and;
  return merged;
};

/**
 * The same filters for a list held whole in the browser (built or aggregated on the client, so the
 * server can't filter it): whether one cell value passes the column's filter. `AdvanceDataTable`
 * uses it as every column's `filterFn` unless `manualFiltering` is on.
 */
export const matchesColumnFilter = (cellValue: unknown, filter: ColumnFilterValue): boolean => {
  if (!isFilterValueActive(filter)) return true;
  switch (filter.type) {
    case 'text':
      return String(cellValue ?? '')
        .toLowerCase()
        .includes(filter.value.trim().toLowerCase());
    case 'options':
      return filter.values.includes(cellValue as ColumnFilterOption['value']);
    case 'date':
    case 'datetime': {
      const value = typeof cellValue === 'string' ? cellValue : '';
      if (!value) return false;
      const day = filter.type === 'date' ? dateOnlyOf(value) : orgCalendarDate(value);
      return (!filter.from || day >= filter.from) && (!filter.to || day <= filter.to);
    }
    case 'time': {
      const value = typeof cellValue === 'string' ? cellValue : '';
      if (!value) return false;
      if (filter.fromDay && orgCalendarDate(value) < filter.fromDay) return false;
      if (filter.toDay && orgCalendarDate(value) > filter.toDay) return false;
      const { hour, minute } = orgWallClock(value);
      const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      if (filter.from && filter.to && filter.to < filter.from) {
        return time >= filter.from || time <= filter.to;
      }
      return (!filter.from || time >= filter.from) && (!filter.to || time <= filter.to);
    }
    case 'number': {
      const value = Number(cellValue);
      if (!Number.isFinite(value)) return false;
      return (
        (filter.min === undefined || value >= filter.min) &&
        (filter.max === undefined || value <= filter.max)
      );
    }
    default:
      return true;
  }
};

/**
 * The rows of a list held whole in the browser that pass the table's column filters, by each
 * column's `accessorFn`. The table filters these rows itself, but its pagination counts from the
 * `totalCount` it is given, so pass `filterRowsByColumns(...).length` there.
 */
export const filterRowsByColumns = <TData>(
  rows: TData[],
  columns: ColumnDef<TData>[],
  filters: ColumnFiltersState
): TData[] => {
  if (filters.length === 0) return rows;
  const tests = filters.map(({ id, value }) => {
    const column = columns.find((candidate) => candidate.id === id);
    const accessor = column && 'accessorFn' in column ? column.accessorFn : undefined;
    return { accessor, filter: value as ColumnFilterValue };
  });
  return rows.filter((row, index) =>
    tests.every(
      ({ accessor, filter }) => !accessor || matchesColumnFilter(accessor(row, index), filter)
    )
  );
};

/** The table's sorting as the query's `order`; the list's default order when nothing is sorted. */
export const buildSortingOrder = (
  sorting: SortingState,
  defaultOrder: OrderBy[] = []
): OrderBy[] =>
  sorting.length > 0
    ? sorting.map(({ id, desc }) => ({ field: id, direction: desc ? 'DESC' : 'ASC' }))
    : defaultOrder;
